import React, { useState, useEffect, useMemo, Suspense, useCallback } from 'react';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import FocusMode from './components/FocusMode';
import DesktopDashboard from './components/DesktopDashboard';
import AffiliateStore from './components/AffiliateStore';
import PerformanceDashboard from './components/PerformanceDashboard';
import AffiliateDisclosure from './components/AffiliateDisclosure';
import LandingPage from './components/LandingPage';
import notificationService from './services/NotificationService';
import { AnalyticsService } from './services/AnalyticsService';
import { lawnCareData, getCurrentMonth } from './data/lawnCareData';
import { getZoneCustomizedData, getZoneAlertsForMonth } from './data/zoneLawnCareData';
import { usePerformanceMonitor, useMemoryMonitor, useWebVitals } from './hooks/usePerformance';

// Detect mobile viewport
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// Lazy load heavy components for better performance
const SettingsModal = React.lazy(() => import('./components/SettingsModal'));
const StateSelectionModal = React.lazy(() => import('./components/StateSelectionModal'));

// Loading component
const LoadingSpinner = () => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-2xl">
      <div className="flex items-center gap-3">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-lawn-500"></div>
        <span className="text-gray-700 dark:text-gray-200 font-medium">Loading...</span>
      </div>
    </div>
  </div>
);

function AppContent() {
  const { settings, updateLocation, updateSetting } = useSettings();

  // Combined month and year state for atomic updates
  const [activeDate, setActiveDate] = useState({
    month: getCurrentMonth(),
    year: new Date().getFullYear()
  });

  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isStateSelectionOpen, setIsStateSelectionOpen] = useState(false);
  const [isStoreOpen, setIsStoreOpen] = useState(false);

  // Dismissed alert keys — lazy-initialized from localStorage so the initial
  // render already has the correct set (no timing/race issues on refresh).
  const [dismissedAlertKeys, setDismissedAlertKeys] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('lawnsmart-dismissed-alerts') || '[]'));
    } catch {
      return new Set();
    }
  });


  // Performance monitoring
  usePerformanceMonitor('AppContent');
  useMemoryMonitor();
  useWebVitals();

  // Get zone-customized data with state-specific modifications
  const zoneCustomizedData = useMemo(() => {
    if (settings.isFirstTimeUser) return lawnCareData;
    return getZoneCustomizedData(settings.hardinessZone, settings.state);
  }, [settings.hardinessZone, settings.state, settings.isFirstTimeUser]);

  // Calculate month-specific stats using zone-customized data
  const monthStats = useMemo(() => {
    const monthData = zoneCustomizedData[activeDate.month];
    if (!monthData) return { total: 0, completed: 0, percentage: 0 };

    let totalTasks = 0;
    const monthTaskIds = [];

    monthData.sections.forEach((section, sectionIndex) => {
      section.tasks.forEach((task, taskIndex) => {
        const taskId = `${activeDate.year}-${activeDate.month}-${sectionIndex}-${taskIndex}`;
        monthTaskIds.push(taskId);
        totalTasks++;
      });
    });

    const completedCount = monthTaskIds.filter(id => completedTasks.has(id)).length;
    const percentage = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;

    return {
      total: totalTasks,
      completed: completedCount,
      percentage: percentage
    };
  }, [activeDate.month, activeDate.year, completedTasks, zoneCustomizedData]);

  // Get zone-specific alerts for current month, filtered by dismissed keys
  const zoneAlerts = useMemo(() => {
    if (settings.isFirstTimeUser) return [];
    const all = getZoneAlertsForMonth(settings.hardinessZone, activeDate.month);
    return all.filter(alert =>
      !dismissedAlertKeys.has(`${activeDate.year}-${activeDate.month}-${alert.title}`)
    );
  }, [settings.hardinessZone, activeDate.month, settings.isFirstTimeUser, activeDate.year, dismissedAlertKeys]);

  const handleDismissAlert = useCallback((alertTitle) => {
    const key = `${activeDate.year}-${activeDate.month}-${alertTitle}`;
    setDismissedAlertKeys(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      try {
        localStorage.setItem('lawnsmart-dismissed-alerts', JSON.stringify([...next]));
      } catch {}
      return next;
    });
  }, [activeDate.year, activeDate.month]);

  // Helper function to find task by ID (memoized)
  const findTaskByTaskId = useCallback((taskId) => {
    const [, monthName, sectionIndex, taskIndex] = taskId.split('-');
    const monthData = zoneCustomizedData[monthName];
    if (monthData && monthData.sections[parseInt(sectionIndex)]) {
      return monthData.sections[parseInt(sectionIndex)].tasks[parseInt(taskIndex)];
    }
    return null;
  }, [zoneCustomizedData]);
  
  // Play completion sound (memoized)
  const playCompletionSound = useCallback(() => {
    // Create a simple completion sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Could not play completion sound:', error);
    }
  }, []);

  // Handle task completion toggle (optimized)
  const handleTaskToggle = useCallback((taskId) => {
    setCompletedTasks(prev => {
      const newSet = new Set(prev);
      const wasCompleted = newSet.has(taskId);
      
      if (wasCompleted) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
        
        // Mark user as no longer first-time when they complete their first task
        if (settings.isFirstTimeUser) {
          updateSetting('isFirstTimeUser', false);
          localStorage.setItem('lawnCareAppUsed', 'true');
        }
        
        // Track task completion in analytics
        const taskData = findTaskByTaskId(taskId);
        if (taskData) {
          AnalyticsService.trackTaskCompletion(
            taskData.text,
            activeDate.month,
            settings.hardinessZone
          );
        }
        
        // Play completion sound if enabled
        if (settings.enableSounds) {
          playCompletionSound();
        }
        
        // Send completion notification if enabled
        if (settings.enableNotifications && notificationService.hasPermission) {
          if (taskData) {
            notificationService.sendCustomReminder(
              '✅ Task Completed!',
              `Great job! You completed: ${taskData.text}`
            );
          }
        }
      }
      
      return newSet;
    });
  }, [settings.enableSounds, settings.enableNotifications, settings.isFirstTimeUser, settings.hardinessZone, activeDate.month, playCompletionSound, findTaskByTaskId, updateSetting]);

  // Handle month change (optimized)
  const handleMonthChange = useCallback((month, year = null) => {
    // Update both month and year together with atomic state update
    if (year !== null) {
      setActiveDate({ month, year });
    } else {
      setActiveDate(prev => ({ ...prev, month }));
    }
    AnalyticsService.trackMonthNavigation(month);
  }, []);

  // Handle current month button (optimized)
  const handleCurrentMonth = useCallback(() => {
    const currentMonth = getCurrentMonth();
    const currentYear = new Date().getFullYear();
    setActiveDate({ month: currentMonth, year: currentYear });
  }, []);


  // Check for first-time user on mount
  useEffect(() => {
    if (settings.isFirstTimeUser) {
      // Double-check that there's no existing app data before showing the modal
      const hasCompletedTasks = localStorage.getItem('lawnCareCompletedTasks');
      const hasAppUsed = localStorage.getItem('lawnCareAppUsed');

      // Check if the localStorage values are actually meaningful (not just empty arrays/objects)
      const tasksAreEmpty = !hasCompletedTasks || hasCompletedTasks === '[]';
      const notUsed = !hasAppUsed;

      // Only show the modal if truly no meaningful data exists
      if (tasksAreEmpty && notUsed) {
        setIsStateSelectionOpen(true);
      }
    }
  }, [settings.isFirstTimeUser]);

  // Load saved data and initialize notifications on mount
  useEffect(() => {
    // Load completed tasks
    const savedTasks = localStorage.getItem('lawnCareCompletedTasks');
    if (savedTasks) {
      try {
        const taskArray = JSON.parse(savedTasks);
        setCompletedTasks(new Set(taskArray));
      } catch (error) {
        console.error('Error loading saved tasks:', error);
      }
    }

    // Initialize notifications if enabled
    if (settings.enableNotifications) {
      notificationService.requestPermission().then(hasPermission => {
        if (hasPermission) {
          // Check for seasonal reminders
          notificationService.scheduleSeasonalReminders();
          
          // Set up weekly reminders if enabled
          if (settings.notificationFrequency === 'weekly' || settings.notificationFrequency === 'daily') {
            notificationService.scheduleWeeklyReminder();
          }
        }
      });
    }
  }, [settings.enableNotifications, settings.notificationFrequency]);

  // Welcome notification for first-time users
  useEffect(() => {
    const hasShownWelcome = localStorage.getItem('lawnCareWelcomeShown');
    if (!hasShownWelcome && settings.enableNotifications && notificationService.hasPermission) {
      setTimeout(() => {
        notificationService.sendNotification('🌱 Welcome to Lawn.Smart!', {
          body: 'You\'ll receive helpful seasonal reminders for optimal lawn care timing.',
          tag: 'welcome'
        });
        localStorage.setItem('lawnCareWelcomeShown', 'true');
      }, 3000);
    }
  }, [settings.enableNotifications]);

  // Save completed tasks to localStorage whenever they change
  useEffect(() => {
    try {
      const taskArray = Array.from(completedTasks);
      localStorage.setItem('lawnCareCompletedTasks', JSON.stringify(taskArray));
    } catch (error) {
      console.error('Error saving tasks:', error);
    }
  }, [completedTasks]);


  const isMobile = useIsMobile();

  // Shared props for both layout components
  const layoutProps = {
    monthName:          activeDate.month,
    activeYear:         activeDate.year,
    monthData:          zoneCustomizedData[activeDate.month],
    completedTasks,
    onTaskToggle:       handleTaskToggle,
    monthStats,
    zoneAlerts,
    zoneCustomizedData,
    onMonthChange:      handleMonthChange,
    onHomeClick:        handleCurrentMonth,
    onDismissAlert:     handleDismissAlert,
    onSettingsClick:    () => setIsSettingsOpen(true),
    onStoreClick:       () => setIsStoreOpen(true),
  };

  return (
    <div style={{ height: '100dvh', overflow: 'hidden' }}>
      {isMobile
        ? <FocusMode {...layoutProps} />
        : <DesktopDashboard {...layoutProps} />
      }

      {isSettingsOpen && (
        <Suspense fallback={<LoadingSpinner />}>
          <SettingsModal
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
          />
        </Suspense>
      )}

      {/* State Selection Modal */}
      {isStateSelectionOpen && (
        <Suspense fallback={<LoadingSpinner />}>
          <StateSelectionModal
            isOpen={isStateSelectionOpen}
            isFirstTime={settings.isFirstTimeUser}
            onSelect={(locationData) => {
              updateLocation({
                state: locationData.state,
                zone: locationData.zone,
                zipCode: locationData.zipCode
              });
              AnalyticsService.trackStateSelection(locationData.state, locationData.zone);
              localStorage.setItem('lawnCareAppUsed', 'true');
            }}
            onClose={() => setIsStateSelectionOpen(false)}
          />
        </Suspense>
      )}

      {/* Affiliate Store Modal */}
      {isStoreOpen && (
        <AffiliateStore
          onClose={() => setIsStoreOpen(false)}
        />
      )}

      {/* Performance Dashboard - Development Only */}
      <PerformanceDashboard />

      {/* Affiliate disclosure — desktop only; mobile Store modal has its own */}
      {!isMobile && <AffiliateDisclosure />}
    </div>
  );
}

function App() {
  const params = new URLSearchParams(window.location.search);
  const showLanding =
    window.location.origin === 'https://www.lawnsmartapp.com' ||
    (process.env.REACT_APP_SHOW_LANDING === 'true' && !params.has('app'));
  if (showLanding) {
    return <LandingPage />;
  }
  return (
    <SettingsProvider>
      <AppContent />
    </SettingsProvider>
  );
}

export default App;